import { useState, type FormEvent } from "react";
import { Button, Input, FormField, Heading, Text } from "@/components/ds";
import styles from "./OnboardingStep.module.css";

interface OnboardingCompanyStepProps {
  readonly userName: string;
  readonly orgName: string;
  readonly onComplete: () => void;
  readonly onSkip: () => void;
}

export function OnboardingCompanyStep({ userName, orgName, onComplete, onSkip }: OnboardingCompanyStepProps) {
  const firstName = userName.split(" ")[0];
  const [name, setName] = useState(orgName);
  const [phone, setPhone] = useState("");
  const [nameError, setNameError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const trimmed = name.trim();
    if (!trimmed || trimmed.length < 2) {
      setNameError("Company name must be at least 2 characters");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("/api/onboarding/company", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: trimmed, phone: phone.trim() || undefined }),
      });

      if (!response.ok) {
        const data = await response.json();
        setNameError(data.error?.fields?.name ?? "Something went wrong. Please try again.");
        return;
      }

      onComplete();
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.header}>
        <Heading level={1} size="h3">Welcome, {firstName}!</Heading>
        <Text size="sm" color="muted">
          Let's get your company set up. This only takes a minute.
        </Text>
      </div>

      <FormField label="Company name" htmlFor="onboarding-company-name" error={nameError}>
        <Input
          name="companyName"
          id="onboarding-company-name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setNameError("");
          }}
          placeholder="e.g. Sparkle Cleaning Ltd"
          error={!!nameError}
        />
      </FormField>

      <FormField label="Your phone number" htmlFor="onboarding-phone" hint="So your team can reach you">
        <Input
          name="phone"
          id="onboarding-phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="e.g. 07700 900000"
        />
      </FormField>

      <div className={styles.actions}>
        <Button type="submit" fullWidth disabled={loading}>
          {loading ? "Saving..." : "Continue"}
        </Button>
        <button type="button" className={styles.skipButton} onClick={onSkip} disabled={loading}>
          I'll do this later
        </button>
      </div>
    </form>
  );
}
